import { Box, Drawer } from "@mui/material";
import React, { useEffect, useRef } from "react";

import VideoEditorSidebar from "./components/VideoEditorSidebar";
import { DRAWER_WIDTH } from "../constants";
import { useAppContext } from "../../hooks/useAppContext";

// 1. Import the Autonomous Brain
import { useKernel } from "../../hooks/AIKernel"; // Adjust relative path as needed

const VideoEditorSidebarWrapper: React.FC = () => {
  const { setIsSidebarClosing, isMobileSidebarOpen, mobileSidebarToggle } =
    useAppContext();

  // 2. Connect to the Agent's state and sensory inputs
  const { agentState, reportBehavior } = useKernel();

  // 3. Adaptive Theme Variables
  const isDarkMode = agentState?.theme === "theme-cinematic-dark";
  const drawerBgColor = isDarkMode ? "#121212" : "#ffffff";
  const drawerTextColor = isDarkMode ? "#ffffff" : "#000000";
  const drawerBorderColor = isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.1)";

  const handleDrawerClose = () => {
    setIsSidebarClosing(true);
    mobileSidebarToggle(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsSidebarClosing(false);
  };

  // 4. TELEMETRY: Track when the mobile user explicitly opens the sidebar
  const prevSidebarState = useRef(isMobileSidebarOpen);
  useEffect(() => {
    if (isMobileSidebarOpen && !prevSidebarState.current) {
      // They just opened it to look for more tools
      if (reportBehavior) {
        reportBehavior("mobile_sidebar_accessed", 2, "interest");
      }
    }
    prevSidebarState.current = isMobileSidebarOpen;
  }, [isMobileSidebarOpen, reportBehavior]);

  return (
    <Box sx={{ height: "100%", position: "relative" }}>
      {/* Mobile Temporary Drawer */}
      <Drawer
        anchor="right"
        variant="temporary"
        open={isMobileSidebarOpen}
        onTransitionEnd={handleDrawerTransitionEnd}
        onClose={handleDrawerClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        PaperProps={{
          // ADAPTIVE UI: Sync background and text colors to the AI's current theme
          sx: {
            backgroundColor: drawerBgColor,
            color: drawerTextColor,
            transition: "background-color 0.4s ease, color 0.4s ease",
          }
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: DRAWER_WIDTH,
          },
        }}
      >
        <VideoEditorSidebar />
      </Drawer>
      
      {/* Desktop Permanent Drawer */}
      <Drawer
        anchor="right"
        variant="permanent"
        PaperProps={{ 
          // ADAPTIVE UI: Add a subtle adaptive border so it blends with the main editor area
          sx: { 
            position: "absolute",
            backgroundColor: drawerBgColor,
            color: drawerTextColor,
            borderLeft: `1px solid ${drawerBorderColor}`,
            transition: "background-color 0.4s ease, color 0.4s ease, border-color 0.4s ease",
          } 
        }}
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: DRAWER_WIDTH,
          },
        }}
        open
      >
        <VideoEditorSidebar />
      </Drawer>
    </Box>
  );
};

export default VideoEditorSidebarWrapper;